import { useEffect, useLayoutEffect, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import type { UseFormReturn } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePortalContext } from '@/contexts/PortalProvider';

import {
  reservationFormMainMachine,
  selectTripLegNextButtonDisabled,
} from '../machines/reservationFormMain.machine';
import type { LevelOfCareContext } from '../machines/levelOfCare.machine';
import {
  createReservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
} from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useDebouncedReservationActionSelectors } from './useDebouncedReservationActionSelectors';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { filterReservationErrorsExcludingMemberPhones } from '../utils/filterReservationErrorsExcludingMemberPhones';
import { mergeAggregatedWithLiveForm } from '../utils/getAggregatedFormValues';

function useDebugReservationMachines(
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>,
) {
  const control = form.control;
  const { t } = useTranslation('common');
  const { isMemberPortal } = usePortalContext();
  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const levelOfCareCtx = levelOfCareSnap.context as LevelOfCareContext;
  const { isNonAmbulatoryByIndex, isCareLevelMismatch, isGurneySelected } =
    levelOfCareCtx;

  const watchedTrips = form.watch('trips');
  const topLevelFormWatch = form.watch([
    'AllowOverride',
    'OverrideFirstName',
    'OverrideLastName',
    'OverrideReason',
    'OverrideServicePlan',
    'MemberHomePhone',
    'MemberMobilePhone',
    'genericErrors',
  ]);
  const watchedForm = form.watch();

  const memberFormSchema = useMemo(
    () => createReservationFormSchema(null, t, isNonAmbulatoryByIndex),
    [t, isNonAmbulatoryByIndex],
  );

  const mergedValues = useMemo(
    () => mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues()),
    [mainSnapshot.context, watchedTrips, topLevelFormWatch, watchedForm],
  );

  const tripLegCount = watchedTrips?.length ?? 0;
  const hasCareLevelMismatch = isCareLevelMismatch
    .slice(0, tripLegCount)
    .some(Boolean);

  /**
   * Keep `tripLegMergedValues` in sync (same as Trip Leg / Member forms) so
   * `selectTripLegNextButtonDisabled` matches the UI.
   */
  useLayoutEffect(() => {
    const existing = mainActor.getSnapshot().context.tripLegMergedValues;
    if (existing !== undefined && isEqual(existing, mergedValues)) {
      return;
    }
    mainActor.send({
      type: 'TRIP_LEG_MERGED_VALUES',
      mergedValues,
    });
  }, [mainActor, mergedValues]);

  const tripLegNextOptions = useMemo(
    () => ({ isMemberPortal, hasCareLevelMismatch }),
    [isMemberPortal, hasCareLevelMismatch],
  );

  const { tripLegNextButtonDisabled } = useDebouncedReservationActionSelectors(
    mainSnapshot,
    tripLegNextOptions,
  );

  const rawTripLegNextDisabled = selectTripLegNextButtonDisabled(
    mainSnapshot,
    tripLegNextOptions,
  );

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  const tripLegMerged = mainSnapshot.context.tripLegMergedValues;
  const blockingServerErrors = filterReservationErrorsExcludingMemberPhones(
    mainSnapshot.context.lastValidationResult?.errors ?? [],
  );
  const allowOverride = Boolean(tripLegMerged?.AllowOverride);
  const isGurneyBlocking = isGurneySelected.some(Boolean) && !allowOverride;

  const memberParseResult = memberFormSchema.safeParse(mergedValues);
  const memberNextDisabled = isSubmitting || !memberParseResult.success;

  const { errors, isValid } = useFormState({ control });

  useEffect(() => {
    const logMain = () => {
      const s = mainActor.getSnapshot();
      console.debug('[reservationFormMain]', s.value, s.context);
    };

    const unsubMain = mainActor.subscribe((snapshot) => {
      console.debug('[reservationFormMain]', snapshot.value, snapshot.context);
    });

    const childEntries = Object.entries(fieldRefs) as [
      keyof typeof fieldRefs,
      (typeof fieldRefs)[keyof typeof fieldRefs],
    ][];

    const unsubsChildren: { unsubscribe: () => void }[] = [];
    for (const [name, ref] of childEntries) {
      if (Array.isArray(ref)) {
        ref.forEach((actor, i) => {
          unsubsChildren.push(
            actor.subscribe((snapshot) => {
              console.debug(
                `[${String(name)}.${i}]`,
                snapshot.value,
                snapshot.context,
              );
            }),
          );
        });
      } else {
        unsubsChildren.push(
          ref.subscribe((snapshot) => {
            console.debug(
              `[${String(name)}]`,
              snapshot.value,
              snapshot.context,
            );
          }),
        );
      }
    }

    logMain();

    return () => {
      unsubMain.unsubscribe();
      unsubsChildren.forEach((u) => u.unsubscribe());
    };
  }, [mainActor, fieldRefs]);

  useEffect(() => {
    console.debug('[reservationForm]', {
      isValid,
      zodErrors: errors,
      mergedValues,
      nextButton: [
        {
          page: 'Trip Leg Form',
          enabled: !tripLegNextButtonDisabled,
          disabled: tripLegNextButtonDisabled,
          rawDisabled: rawTripLegNextDisabled,
          isSubmitting,
          tripLegMergedValuesSet: tripLegMerged !== undefined,
          isGurneyBlocking,
          hasCareLevelMismatch: isMemberPortal && hasCareLevelMismatch,
          blockingServerErrors: blockingServerErrors.length,
          allowOverride,
        },
        {
          page: 'Member Form',
          enabled: !memberNextDisabled,
          disabled: memberNextDisabled,
          isSubmitting,
          zodValid: memberParseResult.success,
        },
      ],
    });
  }, [
    errors,
    isValid,
    mergedValues,
    isSubmitting,
    tripLegNextButtonDisabled,
    rawTripLegNextDisabled,
    memberNextDisabled,
    memberParseResult.success,
    tripLegMerged,
    isGurneyBlocking,
    hasCareLevelMismatch,
    isMemberPortal,
    blockingServerErrors.length,
    allowOverride,
  ]);
}

function ReservationFormDebug({
  form,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
}) {
  useDebugReservationMachines(form);
  return null;
}

export { useDebugReservationMachines, ReservationFormDebug };
